import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FileText, ChevronLeft, ChevronRight, Loader2, Shield, 
  User, Trophy, Settings, Ban, Trash2, Bell, Home
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../contexts/AuthContext";
import adminApi from "../../lib/adminApi";

const ADMIN_EMAIL = "cpt.serdarakdemir@gmail.com";

const ACTION_ICONS = {
  user_banned: Ban,
  user_unbanned: User,
  user_deleted: Trash2,
  league_deleted: Trophy,
  league_status_changed: Trophy,
  settings_updated: Settings,
  announcement_created: Bell,
  announcement_deleted: Bell,
  game_toggled: Settings,
};

const ACTION_COLORS = {
  user_banned: "text-yellow-400",
  user_unbanned: "text-green-400",
  user_deleted: "text-red-400",
  league_deleted: "text-red-400",
  league_status_changed: "text-blue-400",
  settings_updated: "text-[#4DFFFF]",
  announcement_created: "text-green-400",
  announcement_deleted: "text-red-400",
  game_toggled: "text-[#C6A87C]",
  unauthorized_admin_access: "text-red-500",
};

export default function AdminLogs() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [actionFilter, setActionFilter] = useState("");

  useEffect(() => {
    fetchLogs();
  }, [page, actionFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getLogs({ page, limit: 50, action: actionFilter || undefined });
      setLogs(response.data.logs);
      setTotal(response.data.total);
      setPages(response.data.pages);
    } catch (error) {
      console.error("Logs error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Check admin access
  if (user?.email !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Yetkisiz Erişim</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-logs">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/dashboard" className="p-2 rounded-lg bg-[#4DFFFF]/20 hover:bg-[#4DFFFF]/30 text-[#4DFFFF]" title="Ana Sayfa">
          <Home className="w-5 h-5" />
        </Link>
        <Link to="/admin" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Admin Panel">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-[#9333EA]" />
          <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
            Sistem Logları
          </h1>
        </div>
        <span className="ml-auto text-sm text-[#64748B]">{total} kayıt</span>
      </div>

      {/* Filter */}
      <div className="glass-card p-4 mb-6">
        <select
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          className="px-4 py-2 rounded-lg bg-[#050E2E]/50 border border-white/10 text-white"
        >
          <option value="">Tüm İşlemler</option>
          <option value="user_banned">Kullanıcı Banı</option>
          <option value="user_unbanned">Ban Kaldırma</option>
          <option value="user_deleted">Kullanıcı Silme</option>
          <option value="league_deleted">Lig Silme</option>
          <option value="league_status_changed">Lig Durumu</option>
          <option value="settings_updated">Ayar Güncelleme</option>
          <option value="announcement_created">Duyuru Oluşturma</option>
          <option value="unauthorized_admin_access">Yetkisiz Erişim</option>
        </select>
      </div>

      {/* Logs */}
      <div className="glass-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#4DFFFF]" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#B1C1C1]">Henüz log kaydı yok</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {logs.map((log) => {
              const Icon = ACTION_ICONS[log.action] || FileText;
              const colorClass = ACTION_COLORS[log.action] || "text-[#64748B]";
              
              return (
                <div key={log.log_id} className="p-4 hover:bg-white/5">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg bg-white/5 ${colorClass}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-medium">{log.action.replace(/_/g, ' ').toUpperCase()}</span>
                        <span className="text-xs text-[#64748B]">
                          {new Date(log.created_at).toLocaleString("tr-TR")}
                        </span>
                      </div>
                      <p className="text-sm text-[#B1C1C1]">
                        Admin: {log.admin_email || log.admin_id}
                      </p>
                      {log.details && (
                        <pre className="mt-2 p-2 rounded bg-white/5 text-xs text-[#64748B] overflow-x-auto">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      )}
                      <p className="text-xs text-[#64748B] mt-1">IP: {log.ip_address}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t border-white/5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-[#B1C1C1]">{page} / {pages}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              disabled={page === pages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
